import { useAuthenticatedUser } from '@airship-platform/airship-patterns';
import {
  EmptyState,
  FlexBox,
  SanitizedTrimmedHtml,
  SectionLoader,
} from '@airship-platform/jam-web/components';
import { ExclamationIcon } from '@airship-platform/jam-web/icons';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { Fragment, useEffect, useMemo } from 'react';
import { useAppointments } from '../../../data-access/appointments';
import {
  filterAppointments,
  groupAppointmentsByDay,
} from '../utils/filterAppointments.utils';
import BusinessUnitName from './BusinessUnitName';
import ScheduleCard from './ScheduleCard';
import ScheduleCardAction from './ScheduleCardAction';

const ScheduleView: React.FC = () => {
  const { appointments, isLoading, isLoaded, fetchAppointments } =
    useAppointments();
  const { user } = useAuthenticatedUser();

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const groupedAppointments = useMemo(
    () =>
      groupAppointmentsByDay(filterAppointments(appointments, user?.config)),
    [appointments, user?.config]
  );

  if (isLoading && !groupedAppointments.length) {
    return <SectionLoader isLoading title={'Fetching appointments...'} />;
  }

  if (!groupedAppointments.length) {
    return (
      <EmptyState
        title="No appointments available yet"
        subtitle="Please check back later"
      />
    );
  }

  return (
    <FlexBox flexDirection={'column'} gap={3}>
      {groupedAppointments.map((group) => (
        <FlexBox key={group.date} flexDirection={'column'} mb={4} gap={3}>
          <Typography color={'textTertiary'} variant="subtitle1">
            {group.date}
          </Typography>
          {group.appointments.map((appt) => (
            <Fragment key={appt.appointmentId}>
              <ScheduleCard
                appointment={appt.appointment}
                customer={{
                  name: appt.customer.name,
                  address: appt.location.address1,
                  city: appt.location.city,
                  state: appt.location.state,
                  zip: appt.location.zip,
                }}
                cardAction={
                  <ScheduleCardAction
                    appointmentId={appt.appointmentId}
                    color="softGold"
                    children="View Details"
                  />
                }
              >
                <BusinessUnitName businessUnitId={appt.job?.businessUnitId} />
                <FlexBox display={'flex'} gap={1} alignItems={'center'}>
                  {appt.job?.priority === 'HIGH' && (
                    <ExclamationIcon height={24} width={24} />
                  )}
                  <SanitizedTrimmedHtml
                    html={appt.job.summary}
                    hideAirshipLogo={true}
                    maxLength={100}
                    variant="body2"
                  />
                </FlexBox>
              </ScheduleCard>
              <Divider />
            </Fragment>
          ))}
        </FlexBox>
      ))}
      {isLoading && isLoaded && (
        <SectionLoader isLoading title={'Fetching more appointments...'} />
      )}
    </FlexBox>
  );
};

export default ScheduleView;
